import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// Membresía del usuario ACTUAL en su organización. Lee su propia fila de
// organization_members (la RLS `memb_read` permite el self-read: user_id = auth.uid()).
// Sirve para filtrar el sidebar por `allowed_tabs` cuando el usuario es un member
// con permisos limitados. El owner nunca se filtra por aquí (ve todo según su plan).
export type MyMembership = {
  role: 'owner' | 'member';
  // null = ve todas las pestañas; array = solo esas claves (ver src/lib/team-tabs.ts).
  allowed_tabs: string[] | null;
};

export function useMyMembership() {
  return useQuery<MyMembership | null>({
    queryKey: ['my-membership'],
    queryFn: async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id;
      if (!userId) return null;
      // OJO: un usuario puede tener MÁS DE UNA membresía (un colaborador externo es
      // owner de su propia organización y member en la del cliente que le invitó).
      // Con `.maybeSingle()` eso devolvía error de PostgREST (varias filas) y el hook
      // se caía entero, dejando el sidebar sin filtrar. Se piden todas y se elige.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from('organization_members')
        .select('role, allowed_tabs')
        .eq('user_id', userId);
      if (error) throw error;

      const filas = (data as { role: string; allowed_tabs: string[] | null }[] | null) || [];
      if (!filas.length) return null;

      // Manda la membresía MÁS restrictiva: si en alguna organización es member con
      // pestañas limitadas, se respeta ese límite. Ser owner de la propia cuenta no
      // puede servir para saltarse los límites que le puso quien le invitó.
      const limitada = filas.find((f) => f.role !== 'owner' && Array.isArray(f.allowed_tabs));
      const elegida = limitada ?? filas.find((f) => f.role === 'owner') ?? filas[0];

      return {
        role: (elegida.role === 'owner' ? 'owner' : 'member') as MyMembership['role'],
        allowed_tabs: (elegida.allowed_tabs as string[] | null) ?? null,
      };
    },
  });
}
